import { namesFor, principalsInput, scopeMenu, targetsInput } from "../editor/toolbar-ui.js";
import { currentCalendar, readWhen, timeHint, useCalendar, whenOrder, type CalendarShape } from "../editor/event-time.js";
import { newId, NOT_REVEALED, readRows, type KnowsRow as TextKnows, type Placed, type Ref, type Row } from "../editor/rows.js";
import { liveRows, type RowsPart } from "../editor/live-rows.js";

// A thread's, skein's or event's panel in the page editor (docs/tapestry-design.md §3.1, "edited where it's written"), and
// a page's own loose-ends and Ties boxes: everything about the record, editable as soon as its box is in the text. Its
// rows live in the box's text (docs/live-design.md §4): the panel reads them from there and writes every change back
// (a "p9-tapestry-rows" event with the new rows part), so they're saved, drafted and versioned with the page.

type Word = { key: string; label: string; themed: string };
type Words = Record<"threadStatuses" | "stitchStatuses" | "priorities" | "roles" | "knowledge" | "todoStates" | "riskStates" | "eventStatuses", Word[]>;
type Target = { kind: "page" | "skein"; id: string | null; title: string; locked?: boolean };
type Involved = { target: Target; role: string; restrictedTo: string; fromText?: boolean };
type Knows = { target: Target; state: string; note: string; restrictedTo: string };
type Stitch = { id: string; title: string; body: string; status: string; restrictedTo: string; noAi: boolean; knows: Knows[]; waitsFor?: string[]; when?: string; until?: string; by?: string };
type LooseEnd = { id: string; text: string; kind: string; state: string; for: string; restrictedTo: string; noAi: boolean };
type Member = { target: Target; restrictedTo: string; fromText?: boolean };
type TieKind = { id: string; label: string; inverse: string | null };
type TieRow = { id: string; target: Target; kindId: string | null; kindLabel: string | null; note: string; threadId: string | null; restrictedTo: string; noAi: boolean; knows: Knows[]; since?: string; until?: string };
type Named = { id: string; title: string };
type Box = {
  kind: "thread" | "skein" | "event" | "looseends" | "ties";
  id: string;
  title: string;
  status?: string;
  priority?: string;
  involved?: Involved[];
  stitches?: Stitch[];
  knows: Knows[];
  looseEnds?: LooseEnd[];
  members?: Member[];
  ties?: TieRow[];
  incoming?: Array<{ reads: string; from: Target; via: string | null; note: string }>;
  unanswered?: Target[];
  threads?: Array<{ id: string; title: string }>;
  /** An event's fence (its time, what it's placed after, its thread…): what the text says. */
  settings?: Record<string, string>;
  /** Names of the thread and the event the fence points at, when the person may see them. */
  thread?: Named | null;
  anchor?: Named | null;
  instead?: Named | null;
};

/** What the server knows about a record that isn't in its rows: links in its text, ties coming in, names for its fence. */
type State = Partial<Box> & { involved?: Array<Involved & { fromText?: boolean }>; members?: Array<Member & { fromText?: boolean }> };

const states = new Map<string, State>();
let words: Words | null = null;
let tieKinds: TieKind[] = [];
/** The calendars of the page's group (a box can be written in another of them), and the page's own. */
let calendarList: Array<{ title: string; shape: CalendarShape | null }> = [];
let pageCalendarTitle = "";
const waiting = new Map<string, Array<() => void>>();
let timer: number | undefined;
const base = (): string => document.body.dataset.base ?? "/";
/** Page and skein names by id (null: one the person can't see). */
const names = new Map<string, string | null>();

/** Loads what the server knows of records not seen yet (batched), and the words, once. */
function load(id: string, done: () => void): void {
  if ((states.has(id) || !/^((page|ties):)?[0-9a-f-]{36}$/i.test(id)) && words) return done();
  waiting.set(id, [...(waiting.get(id) ?? []), done]);
  window.clearTimeout(timer);
  timer = window.setTimeout(async () => {
    const ids = [...waiting.keys()].filter((x) => !states.has(x) && /^((page|ties):)?[0-9a-f-]{36}$/i.test(x));
    const callbacks = [...waiting.values()].flat();
    waiting.clear();
    try {
      // The page whose calendar dates are written in (the one being edited, or the parent of a new one).
      const editor = document.querySelector<HTMLElement>("p9-editor");
      const page = editor?.dataset.editingPage || editor?.dataset.pageId || "";
      const response = await fetch(`${base()}tapestry/state?ids=${ids.join(",")}${page ? `&calendarOf=${page}` : ""}`);
      const result = (await response.json()) as {
        records: Array<Box & { involved?: Involved[]; members?: Member[] }>; words: Words; tieKinds: TieKind[]; calendar?: CalendarShape | null;
        calendars?: Array<{ title: string; shape: CalendarShape | null }>; calendarTitle?: string;
      };
      words = result.words;
      useCalendar(result.calendar);
      calendarList = result.calendars ?? [];
      pageCalendarTitle = result.calendarTitle ?? "";
      tieKinds = result.tieKinds ?? [];
      for (const record of result.records) {
        // A page's own loose ends are kept under "page:<id>", its ties under "ties:<id>".
        const key = record.kind === "looseends" ? `page:${record.id}` : record.kind === "ties" ? `ties:${record.id}` : record.id;
        states.set(key, record);
        for (const t of [...(record.involved ?? []), ...(record.members ?? [])].map((r) => r.target)) if (t.id) names.set(t.id, t.locked ? null : t.title);
      }
    } catch {
      // Without the server's answer the panels show what the text says; saving keeps it.
    }
    for (const callback of callbacks) callback();
  }, 30);
}

// ------------------------------------------------------------------ the rows in the text and the panel's model

const toTarget = (r: Ref): Target => ({ kind: r.skein ? "skein" : "page", id: r.id, title: names.get(r.id) ?? (names.has(r.id) ? "" : "…"), locked: names.get(r.id) === null });
const toRef = (t: Target): Ref => ({ skein: t.kind === "skein", id: t.id! });
const knowsOf = (k: TextKnows, restrictedTo = ""): Knows => ({ target: toTarget(k.ref), state: k.state, note: k.note, restrictedTo });
const knowsRow = (k: Knows): TextKnows => ({ type: "knows", state: k.state, ref: toRef(k.target), note: k.note });

// Rows of one kind in their order: as written, those in another block placed after the row they name (as the server does).
function ordered(placed: Placed[], idOf: (row: Row) => string | null): Placed[] {
  const after = (p: Placed) => ("settings" in p.row ? p.row.settings.after : undefined)?.trim() ?? "";
  const list = placed.filter((p) => !after(p));
  const last = new Map<string, Placed>();
  for (const p of placed.filter((x) => after(x))) {
    const target = after(p).toLowerCase();
    const previous = last.get(target);
    const at = previous ? list.indexOf(previous) + 1 : target === "start" ? 0 : (() => {
      const i = list.findIndex((x) => idOf(x.row) === target);
      return i < 0 ? list.length : i + 1;
    })();
    list.splice(at, 0, p);
    last.set(target, p);
  }
  return list;
}

/** The panel's model of a box from its rows (and what the server knows besides). */
function fromRows(kind: Box["kind"], id: string, placed: Placed[], state: State | undefined): Box {
  const box: Box = { kind, id, title: "", knows: [], ...pick(state) };
  const of = <T extends Row["type"]>(type: T) => placed.filter((p) => p.row.type === type) as Array<Placed & { row: Extract<Row, { type: T }> }>;
  box.knows = of("knows").map((p) => knowsOf(p.row, p.to ?? ""));
  if (kind === "thread" || kind === "event")
    box.involved = [
      ...(state?.involved ?? []).filter((i) => i.fromText),
      ...of("involved").map((p) => ({ target: toTarget(p.row.ref), role: p.row.role, restrictedTo: p.to ?? "" })),
    ];
  if (kind === "thread")
    box.stitches = (ordered(of("stitch"), (r) => (r.type === "stitch" ? r.id : null)) as Array<Placed & { row: Extract<Row, { type: "stitch" }> }>).map((p) => ({
      id: p.row.id ?? newId(), title: p.row.title, body: p.row.body, status: p.row.status, restrictedTo: p.to ?? "", noAi: p.noAi,
      knows: p.row.knows.map((k) => knowsOf(k)), waitsFor: (p.row.settings.waits ?? "").split(/\s+/).filter(Boolean),
      when: p.row.settings.when, until: p.row.settings.until, by: p.row.settings.by,
    }));
  if (kind === "thread" || kind === "looseends")
    box.looseEnds = (ordered(of("looseend"), (r) => (r.type === "looseend" ? r.id : null)) as Array<Placed & { row: Extract<Row, { type: "looseend" }> }>).map((p) => ({
      id: p.row.id ?? newId(), text: p.row.text, kind: p.row.kind, state: p.row.state, for: p.row.settings.for ?? "", restrictedTo: p.to ?? "", noAi: p.noAi,
    }));
  if (kind === "skein")
    box.members = [...(state?.members ?? []).filter((m) => m.fromText), ...of("member").map((p) => ({ target: toTarget(p.row.ref), restrictedTo: p.to ?? "" }))];
  if (kind === "ties")
    box.ties = (ordered(of("tie"), (r) => (r.type === "tie" ? r.id : null)) as Array<Placed & { row: Extract<Row, { type: "tie" }> }>).map((p) => ({
      id: p.row.id ?? newId(), target: toTarget(p.row.ref), kindLabel: p.row.kindLabel,
      kindId: tieKinds.find((k) => k.label.toLowerCase() === (p.row.kindLabel ?? "").toLowerCase())?.id ?? null,
      note: p.row.note, threadId: p.row.settings.thread ?? null, restrictedTo: p.to ?? "", noAi: p.noAi, knows: p.row.knows.map((k) => knowsOf(k)),
      since: p.row.settings.since, until: p.row.settings.until,
    }));
  return box;
}

// The server's parts of a record (not its rows): the panel's model starts from these.
function pick(state: State | undefined): Partial<Box> {
  if (!state) return {};
  const { incoming, unanswered, threads, thread, anchor, instead, title, status, priority } = state;
  return { incoming, unanswered, threads, thread, anchor, instead, title: title ?? "", status, priority };
}

const settings = (values: Record<string, string | undefined | null>): Record<string, string> =>
  Object.fromEntries(Object.entries(values).filter((e): e is [string, string] => Boolean(e[1]?.trim())).map(([k, v]) => [k, v.trim()]));
const placedTo = (restrictedTo: string): string | null => (restrictedTo.trim() ? restrictedTo.trim() : null);

// Rows in another block than the open rows say where they go among those: after the nearest open one before them.
function afters<T extends { id: string; restrictedTo: string; noAi?: boolean }>(list: T[]): Map<string, string | undefined> {
  const result = new Map<string, string | undefined>();
  let lastOpen: string | null = null;
  for (const row of list) {
    const open = !row.restrictedTo.trim() && !row.noAi;
    result.set(row.id, open ? undefined : (lastOpen ?? "start"));
    if (open) lastOpen = row.id;
  }
  return result;
}

/** The panel's model of a box as rows for its text. */
function toRows(b: Box): Placed[] {
  const rows: Placed[] = [];
  for (const i of b.involved ?? []) if (!i.fromText && i.target.id) rows.push({ row: { type: "involved", role: i.role, ref: toRef(i.target) }, to: placedTo(i.restrictedTo), noAi: false });
  const stitchAfter = afters(b.stitches ?? []);
  for (const s of b.stitches ?? [])
    rows.push({
      row: {
        type: "stitch", id: s.id, status: s.status, title: s.title.trim() || "New stitch", body: s.body, knows: s.knows.filter((k) => k.target.id).map(knowsRow),
        settings: settings({ when: s.when, until: s.until, by: s.by, waits: (s.waitsFor ?? []).join(" "), after: stitchAfter.get(s.id) }),
      },
      to: placedTo(s.restrictedTo), noAi: s.noAi,
    });
  for (const k of b.knows) if (k.target.id) rows.push({ row: knowsRow(k), to: placedTo(k.restrictedTo), noAi: false });
  const endAfter = afters(b.looseEnds ?? []);
  for (const l of b.looseEnds ?? [])
    if (l.text.trim())
      rows.push({ row: { type: "looseend", id: l.id, kind: l.kind, state: l.state, text: l.text, settings: settings({ for: l.for, after: endAfter.get(l.id) }) }, to: placedTo(l.restrictedTo), noAi: l.noAi });
  for (const m of b.members ?? []) if (!m.fromText && m.target.id) rows.push({ row: { type: "member", ref: toRef(m.target) }, to: placedTo(m.restrictedTo), noAi: false });
  const tieAfter = afters(b.ties ?? []);
  for (const t of b.ties ?? [])
    if (t.target.id)
      rows.push({
        row: {
          type: "tie", id: t.id, kindLabel: t.kindId ? (tieKinds.find((k) => k.id === t.kindId)?.label ?? t.kindLabel) : t.kindLabel, ref: toRef(t.target), note: t.note,
          knows: t.knows.filter((k) => k.target.id).map(knowsRow), settings: settings({ since: t.since, until: t.until, thread: t.threadId, after: tieAfter.get(t.id) }),
        },
        to: placedTo(t.restrictedTo), noAi: t.noAi,
      });
  return rows;
}

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { class?: string } = {}, ...children: Array<Node | string | null>): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const { class: className, ...rest } = props;
  if (className) el.className = className;
  Object.assign(el, rest);
  for (const child of children) if (child !== null) el.append(child);
  return el;
}

export class P9TapestryBox extends HTMLElement {
  #box!: Box;
  #description: HTMLTextAreaElement | null = null;
  /** The rows part as the text has it (the last one read, or written from here). */
  #rows = "";
  /** Placeholders for rows the person can't see: kept as they are. */
  #hidden: string[] = [];
  #problems: string[] = [];
  /** Restricted rows this person may see while writing live: parts of their own, shown in place (live-rows.ts). */
  #parts: RowsPart[] = [];

  /** Markdown mode: the box's fence was changed in the editor (typed by hand): an event's panel follows it. */
  syncSettings(json: string): void {
    if (this.#box?.kind !== "event" || this.dataset.settings === json) return;
    this.dataset.settings = json;
    if (this.contains(document.activeElement)) return;
    this.#box.settings = this.#fence();
    this.#render();
  }

  // The settings on the box's fence, as the editor passed them.
  #fence(): Record<string, string> {
    try {
      return JSON.parse(this.dataset.settings ?? "{}") as Record<string, string>;
    } catch {
      return {};
    }
  }

  /** Markdown mode: the box's text changed in the editor; show it here unless it's being typed here. */
  syncText(text: string): void {
    this.dataset.text = text;
    if (this.#description && document.activeElement !== this.#description) this.#description.value = text;
  }

  /** The box's rows part changed in the text (typed in Markdown mode, an undo): the panel follows it. */
  syncRows(rows: string): void {
    if (rows === this.#rows) return;
    this.dataset.rows = rows;
    if (!this.#box) return;
    this.#read();
    this.#render();
  }

  connectedCallback(): void {
    const id = this.dataset.id ?? "";
    if ((this.dataset.kind === "looseends" || this.dataset.kind === "ties") && !/^(page|ties):[0-9a-f-]{36}$/i.test(id)) {
      this.replaceChildren(h("p", { class: "muted small" }, `${this.dataset.kind === "ties" ? "Ties" : "Loose ends"} for this page can be added here once the page has been saved.`));
      return;
    }
    if (!/^((page|ties):)?[0-9a-f-]{36}$/i.test(id)) return;
    this.replaceChildren(h("p", { class: "muted small" }, "Loading…"));
    load(id, () => {
      this.#read();
      this.#render();
      void this.#nameTargets();
    });
    // Restricted rows opened (or changed) in a live session: shown again.
    document.addEventListener("p9-live-rows", this.#liveRowsChanged);
    // Enter in a panel field must not submit the page.
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" || (event.target as HTMLElement).tagName !== "INPUT") return;
      event.preventDefault();
      event.target!.dispatchEvent(new Event("change"));
    });
  }

  disconnectedCallback(): void {
    document.removeEventListener("p9-live-rows", this.#liveRowsChanged);
  }

  #liveRowsChanged = (): void => {
    if (!this.#box || this.contains(document.activeElement)) return;
    this.#read();
    this.#render();
  };

  // The model from the box's rows part and fence (the text is where they live), and what the server knows besides.
  #read(): void {
    const id = this.dataset.id ?? "";
    const kind = (["skein", "event", "looseends", "ties"].includes(this.dataset.kind ?? "") ? this.dataset.kind : "thread") as Box["kind"];
    this.#rows = this.dataset.rows ?? "";
    const expanded = liveRows.get().expand(this.#rows);
    this.#parts = expanded.parts;
    const rows = readRows(expanded.rows);
    this.#hidden = rows.hidden;
    this.#problems = rows.problems;
    const box = fromRows(kind, id, rows.placed, states.get(id));
    const fromText = (list: Word[] | undefined, written: string | undefined) =>
      written ? list?.find((w) => [w.key, w.themed, w.label, w.label.replace(/^.*\((.*)\)$/, "$1")].some((x) => x.toLowerCase() === written.toLowerCase().replace(/-/g, " ") || x === written))?.key : undefined;
    if (this.dataset.title) box.title = this.dataset.title;
    box.settings = this.#fence();
    if (kind === "thread") {
      box.status = fromText(words?.threadStatuses, this.dataset.status) ?? box.status ?? "spun";
      box.priority = fromText(words?.priorities, this.dataset.priority) ?? box.priority ?? "normal";
    }
    this.#box = box;
  }

  // Names for the pages and skeins the rows point at (the text has only their ids).
  async #nameTargets(): Promise<void> {
    const ids = [...this.#rows.matchAll(/(?:page|skein):([0-9a-f-]{36})/gi)].map((m) => m[1]!.toLowerCase()).filter((x) => !names.has(x));
    if (ids.length === 0) return;
    const found = await namesFor([...new Set(ids)]);
    for (const id of ids) names.set(id, found[id] ?? null);
    this.#read();
    this.#render();
  }

  // Name, status and priority (and an event's time and the rest) are written into the box's fence by the editor (text is
  // where they live).
  #setting(key: string, value: string): void {
    this.dispatchEvent(new CustomEvent("p9-tapestry-param", { bubbles: true, detail: { id: this.#box.id, key, value } }));
  }

  // A change in the panel: the rows part written anew and handed to the editor, which puts it in the box's text.
  #changed(rerender = true): void {
    this.#rows = liveRows.get().collapse(toRows(this.#box), this.#hidden, this.#parts);
    this.dataset.rows = this.#rows;
    this.dispatchEvent(new CustomEvent("p9-tapestry-rows", { bubbles: true, detail: { id: this.#box.id, rows: this.#rows } }));
    this.dispatchEvent(new CustomEvent("p9-tapestry-change", { bubbles: true }));
    if (rerender) this.#render();
  }

  #render(): void {
    const b = this.#box;
    if (!words) {
      this.replaceChildren(h("p", { class: "muted small" }, "The panel couldn't load; the text is saved as usual."));
      return;
    }
    if (this.#problems.length) {
      // Rows that can't be read are left to the text (Markdown mode), never rewritten from here.
      this.replaceChildren(h("div", { class: "tpe" }, h("p", { class: "notice error small" },
        "Some rows in this box's text can't be read, so the panel leaves them alone until they're fixed: ", this.#problems.slice(0, 3).join("; "))));
      return;
    }
    if (b.kind === "ties") {
      this.replaceChildren(h("div", { class: "tpe" }, this.#ties()));
      return;
    }
    if (b.kind === "looseends") {
      // A page's own loose ends (those of its threads are in the threads' panels).
      const head = h("div", { class: "tpe-head" }, h("span", { class: "tp-kind" }, "Loose ends on this page"),
        h("a", { class: "tpe-help", href: `${base()}tapestry/help`, target: "_blank", title: "How loose ends work", textContent: "?" }));
      this.replaceChildren(h("div", { class: "tpe" }, head, this.#looseEnds()));
      return;
    }
    const title = h("input", { class: "tpe-title", type: "text", value: b.title, maxLength: 200, placeholder: `Name of the ${b.kind}` });
    title.addEventListener("change", () => {
      b.title = title.value;
      this.#setting("title", title.value);
    });
    const head = h("div", { class: "tpe-head" }, h("span", { class: "tp-kind" }, { thread: "Thread", skein: "Skein", event: "Event" }[b.kind as "thread"] ?? "Skein"), title);
    if (b.kind === "event") {
      const status = b.settings?.status && words.eventStatuses.some((w) => w.key === b.settings!.status) ? b.settings.status : "planned";
      head.append(this.#menu(words.eventStatuses, status, (key) => this.#put("status", key, true), "Status: what's canon, planned or only possible"));
    }
    if (b.kind === "thread") {
      head.append(
        this.#menu(words.threadStatuses, b.status ?? "spun", (key) => {
          b.status = key;
          this.#setting("status", words!.threadStatuses.find((w) => w.key === key)?.themed ?? key);
          this.#render();
        }, "Status"),
        this.#menu(words.priorities, b.priority ?? "normal", (key) => {
          b.priority = key;
          this.#setting("priority", key === "normal" ? "" : key);
          this.#render();
        }, "Priority"),
      );
    }
    const noAi = h("input", { type: "checkbox", checked: this.dataset.ai === "no" });
    noAi.addEventListener("change", () => {
      this.dataset.ai = noAi.checked ? "no" : "";
      this.#setting("ai", noAi.checked ? "no" : "");
    });
    head.append(h("label", { class: "tpe-noai small", title: "Never shown to any AI: not its text, panel or links. Otherwise it follows the page's AI setting." }, noAi, " never shown to AI"));
    head.append(h("a", { class: "tpe-help", href: `${base()}tapestry/help`, target: "_blank", title: "How threads, skeins and events work", textContent: "?" }));
    const sections: HTMLElement[] = [head];
    // Markdown mode: the box's text (its description) is edited here too; in Visual mode it's the box itself.
    if (this.dataset.mode === "markdown") {
      const description = h("textarea", { class: "tpe-description", rows: 3, value: this.dataset.text ?? "",
        placeholder: { thread: "What is this thread about? (shown wherever the thread appears)", event: "What happens? (shown wherever the event appears)" }[b.kind as "thread"] ?? "Who belongs, and why" });
      description.addEventListener("input", () =>
        this.dispatchEvent(new CustomEvent("p9-tapestry-text", { bubbles: true, detail: { id: b.id, value: description.value } })));
      this.#description = description;
      const wrap = (open: string) => {
        const [from, to] = [description.selectionStart, description.selectionEnd];
        const part = description.value.slice(from, to).trim() || "A detail";
        description.setRangeText(`\n${open}\n${part}\n:::\n`, from, to, "end");
        description.dispatchEvent(new Event("input"));
      };
      const restricted = h("button", { type: "button", class: "small", textContent: "🔒 Restricted part",
        title: "Keep the selected part (or a new part) from players: only those you name see it. Fill in to=\"…\" with @people or #groups." });
      restricted.addEventListener("click", () => {
        const editor = this.closest<HTMLElement>("p9-editor");
        const { element, input } = principalsInput({ lookupUrl: editor?.dataset.lookupUrl ?? `${base()}lookup`, me: editor?.dataset.me, onChange: () => {} });
        input.placeholder = "Who may see it: @person #group";
        const ok = h("button", { type: "button", class: "small", textContent: "Restrict" });
        ok.addEventListener("click", () => {
          pickSlot.replaceChildren();
          wrap(`:::restricted {to="${input.value.trim().replace(/"/g, "")}"}`);
        });
        pickSlot.replaceChildren(element, ok);
        input.focus();
      });
      const unknown = h("button", { type: "button", class: "small", textContent: "👁 Unbeknownst to…",
        title: "Mark the selected part (or a new part) as something some characters don't know. Story information, not a permission." });
      const pickSlot = h("span");
      // Who (several pages or skeins), then the part gets wrapped.
      const askWho = (directive: string, verb: string) => {
        const editor = this.closest<HTMLElement>("p9-editor");
        const { element, value } = targetsInput(`${editor?.dataset.lookupUrl ?? `${base()}lookup`}?near=${encodeURIComponent(editor?.dataset.editingPage ?? "")}`);
        const ok = h("button", { type: "button", class: "small", textContent: verb });
        ok.addEventListener("click", () => {
          if (!value.value) return;
          pickSlot.replaceChildren();
          wrap(`:::${directive} {to="${value.value}"}`);
        });
        pickSlot.replaceChildren(element, ok);
      };
      unknown.addEventListener("click", () => askWho("unbeknownst", "Mark"));
      const knownTo = h("button", { type: "button", class: "small", textContent: "🤫 Known only to…",
        title: "Mark the selected part (or a new part) as something only some characters know. Story information, not a permission." });
      knownTo.addEventListener("click", () => askWho("knownto", "Mark"));
      sections.push(this.#section("Description", "The text inside the box on the page: what this is about. Shown wherever it appears.", description,
        h("div", { class: "tpe-add" }, restricted, unknown, knownTo, pickSlot)));
    }
    if (b.kind === "thread") {
      sections.splice(1, 0, this.#fenceTimes([["since", "active since"], ["until", "until"], ["by", "deadline (by)"]],
        "Optional: since when it's pursued, until when, and a deadline. Timelines show them; a deadline passed is listed as overdue."));
      sections.push(this.#involved(), this.#stitches(), this.#knowsSection(b.knows, "Who knows of it"), this.#looseEnds());
    } else if (b.kind === "event") {
      sections.splice(1, 0, this.#when());
      sections.push(this.#alternatives(), this.#involved(), this.#knowsSection(b.knows, "Who knows of it"));
    } else {
      sections.splice(1, 0, this.#fenceTimes([["since", "formed"], ["until", "until"]], "Optional: since when the group holds, and until when. Timelines show them."));
      sections.push(this.#members(), this.#knowsSection(b.knows, "Who knows of it"));
    }
    this.replaceChildren(h("div", { class: "tpe" }, ...sections));
  }

  // The calendar this box's dates are written in: the one its fence names, else the page's (ties and loose ends: the page's).
  #shape(): CalendarShape | null {
    const named = this.#box && ["thread", "skein", "event"].includes(this.#box.kind) ? this.#box.settings?.calendar : undefined;
    if (!named) return currentCalendar();
    return calendarList.find((c) => c.title.toLowerCase() === named.toLowerCase())?.shape ?? currentCalendar();
  }

  // "Written in": another calendar of the page's group, for a part that happens there (a Horizon Realm's day on a page in
  // Earth dates). Shown only when there is another.
  #calendarMenu(): HTMLElement | null {
    if (calendarList.length < 2) return null;
    const list: Word[] = calendarList.map((c) => ({ key: c.title, label: c.title, themed: c.title }));
    const current = calendarList.find((c) => c.title.toLowerCase() === (this.#box.settings?.calendar ?? pageCalendarTitle).toLowerCase())?.title ?? pageCalendarTitle;
    return h("span", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Written in" }),
      this.#menu(list, current, (key) => this.#put("calendar", key === pageCalendarTitle ? "" : key, true), "The calendar its dates are written in"));
  }

  // An event's setting: kept here and written into its fence (empty removes it).
  #put(key: string, value: string, rerender = false): void {
    const settings = (this.#box.settings ??= {});
    if (value) settings[key] = value;
    else delete settings[key];
    this.#setting(key, value);
    if (rerender) this.#render();
  }

  // When an event happens (docs/tapestry-design.md §3.5): a date, an end, a date in words, or a place among other
  // events; the thread it's part of. All of it is written on the box's fence. (Now is the chronicle's, set on timelines.)
  #when(): HTMLElement {
    const b = this.#box;
    const set = b.settings ?? {};
    const reads = h("span", { class: "tpe-reads small" });
    const check = (): void => {
      const start = when.value.trim();
      const end = until.value.trim();
      const parts: string[] = [];
      let bad = false;
      if (start) {
        const read = readWhen(start, this.#shape());
        parts.push(read ? `reads as ${read}` : `not a date I can read: write ${timeHint(this.#shape())}`);
        bad ||= !read;
      }
      if (end) {
        const read = readWhen(end, this.#shape());
        if (!read) parts.push("the end isn't a date I can read");
        else if (!start) parts.push("an end needs a start");
        else if (whenOrder(end, this.#shape())! < (whenOrder(start, this.#shape()) ?? 0)) parts.push("it ends before it starts");
        else parts.push(`until ${read}`);
        bad ||= !read || !start || (whenOrder(end, this.#shape()) ?? 0) < (whenOrder(start, this.#shape()) ?? 0);
      }
      reads.textContent = parts.join("; ");
      reads.classList.toggle("tpe-bad", bad);
    };
    const field = (key: string, placeholder: string, hint: string): HTMLInputElement => {
      const input = h("input", { type: "text", value: set[key] ?? "", placeholder, title: hint, class: "tpe-when" });
      input.addEventListener("input", check);
      input.addEventListener("change", () => this.#put(key, input.value.trim(), key === "when"));
      return input;
    };
    const when = field("when", "when: 1912-03-03 21:00", `When it starts: ${timeHint(this.#shape())} (as exact as you know it)`);
    const until = field("until", "until (optional)", "When it ends, for something that takes a while (a three-day ritual)");
    // An approximate time: "around 1912", drawn fuzzy on timelines.
    const around = h("input", { type: "checkbox", checked: /^(yes|true)$/i.test(set.around ?? "") });
    around.addEventListener("change", () => this.#put("around", around.checked ? "yes" : "", true));
    const aroundLabel = h("label", { class: "small", title: "An approximate time: shown as “around …”" }, around, " around");
    const label = field("label", "written as (optional), e.g. 3rd of Frostfall, 1012 AR",
      "Shown instead of the date. Without a date, it's the only date there is: place it after or before another event below.");
    label.classList.add("tpe-wide");
    check();
    const rows: Array<HTMLElement | null> = [this.#calendarMenu(), h("div", { class: "tpe-add" }, when, until, aroundLabel, reads), h("div", { class: "tpe-add" }, label)];

    // Without a date: where it goes among the other events.
    if (!set.when) {
      const after = set.after ? "after" : set.before ? "before" : "";
      const anchorId = set.after || set.before || "";
      const anchorName = anchorId && b.anchor?.id === anchorId ? b.anchor.title : anchorId ? "an event" : "";
      if (anchorId) {
        // How far after (or before) it: "3 years" gives it a time of its own, worked out from that event's and moving with it.
        const offset = h("input", { type: "text", value: set.offset ?? "", placeholder: "right", class: "tpe-short",
          title: "How long after (or before): 3 years, 2 months 10 days, 1 week, 30 minutes. Empty: right after, with no date of its own." });
        offset.addEventListener("change", () => this.#put("offset", offset.value.trim(), true));
        rows.push(h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Comes" }), offset,
          h("span", { class: "muted small", textContent: ` ${after} ` }),
          h("a", { href: `${base()}tapestry/event/${anchorId}`, target: "_blank", textContent: anchorName }),
          this.#remove(() => ((b.anchor = null), this.#put("offset", ""), this.#put(after, "", true)), "the placement")),
          h("p", { class: "muted small", textContent: set.offset
            ? "Its time is worked out from that event's when the page is saved, and moves with it."
            : "Right after it, with no date of its own; give a time (3 years, 2 days…) to work one out." }));
      } else {
        let side = "after";
        const sides: Word[] = [{ key: "after", label: "comes right after", themed: "after" }, { key: "before", label: "comes right before", themed: "before" }];
        const sideMenu = (): HTMLElement => this.#menu(sides, side, (key) => {
          side = key;
          place.replaceChild(sideMenu(), place.firstChild!);
        }, "Placed after or before");
        const place = h("div", { class: "tpe-add" }, sideMenu(), this.#recordPicker("events", "another event…", (picked) => {
          if (picked.id === b.id) return;
          b.anchor = picked;
          this.#put(side === "after" ? "before" : "after", "");
          this.#put(side, picked.id, true);
        }));
        rows.push(h("p", { class: "muted small", textContent: "No date: place it among the other events (and say how long after, if you know), or it's listed as undated." }), place);
      }
    }

    const threadId = set.thread ?? "";
    rows.push(threadId
      ? h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Part of the thread " }),
          h("a", { href: `${base()}tapestry/thread/${threadId}`, target: "_blank", textContent: b.thread?.id === threadId ? b.thread.title : "a thread" }),
          this.#remove(() => ((b.thread = null), this.#put("thread", "", true)), "from the thread"))
      : h("div", { class: "tpe-add" }, this.#recordPicker("threads", "Part of a thread… (optional)", (picked) => {
          b.thread = picked;
          this.#put("thread", picked.id, true);
        })));
    return this.#section("When", "Its time, as exact as you know it; or a date in words placed among the other events.", ...rows);
  }

  // Alternatives and conditions (2b part 4): the event this one happens instead of (at most one of them happens), and
  // what it hangs on, in words. Both are written on the fence.
  #alternatives(): HTMLElement {
    const b = this.#box;
    const set = b.settings ?? {};
    const insteadId = set.instead ?? "";
    const instead = insteadId
      ? h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Happens instead of " }),
          h("a", { href: `${base()}tapestry/event/${insteadId}`, target: "_blank", textContent: b.instead?.id === insteadId ? b.instead.title : "an event" }),
          this.#remove(() => ((b.instead = null), this.#put("instead", "", true)), "the alternative"))
      : h("div", { class: "tpe-add" }, this.#recordPicker("events", "Happens instead of… (optional)", (picked) => {
          if (picked.id === b.id) return;
          b.instead = picked;
          this.#put("instead", picked.id, true);
        }));
    const condition = h("input", { type: "text", value: set.condition ?? "", maxLength: 300, class: "tpe-wide",
      placeholder: "condition (optional), e.g. unless the PCs intervene", title: "What it hangs on, in words: shown with it wherever it appears" });
    condition.addEventListener("change", () => this.#put("condition", condition.value.trim()));
    return this.#section("Alternatives and conditions",
      "An alternative: this happens instead of another event (the eruption, or the eruption prevented). At most one of them can have happened; once one has, the others show as not taken.",
      instead, h("div", { class: "tpe-add" }, condition));
  }

  // A time field (2b): written like an event's time, with how it reads as you type; nothing is forced.
  #timeInput(value: string, placeholder: string, title: string, onChange: (value: string) => void): HTMLElement {
    const input = h("input", { type: "text", value, placeholder, title, class: "tpe-when" });
    const reads = h("span", { class: "tpe-reads small" });
    const check = () => {
      const text = input.value.trim();
      const read = text ? readWhen(text, this.#shape()) : null;
      reads.textContent = !text ? "" : read ?? "not a date I can read";
      reads.classList.toggle("tpe-bad", Boolean(text) && !read);
    };
    input.addEventListener("input", check);
    input.addEventListener("change", () => onChange(input.value.trim()));
    check();
    return h("span", { class: "tpe-time" }, input, reads);
  }

  // A thread's or skein's times, written on its fence like its name.
  #fenceTimes(keys: Array<[key: string, label: string]>, hint: string): HTMLElement {
    const set = this.#box.settings ?? {};
    return this.#section("Times", hint, ...[this.#calendarMenu()].filter((x): x is HTMLElement => x !== null), h("div", { class: "tpe-add" },
      ...keys.map(([key, label]) => this.#timeInput(set[key] ?? "", label, `${label}: ${timeHint(this.#shape())}`, (value) => this.#put(key, value)))));
  }

  // A thread or an event, picked from suggestions (nearest frame first).
  #recordPicker(kind: "threads" | "events", placeholder: string, pick: (picked: Named) => void): HTMLElement {
    const editor = this.closest<HTMLElement>("p9-editor");
    const near = editor?.dataset.editingPage || editor?.dataset.pageId || "";
    const box = h("p9-autocomplete" as "div");
    box.dataset.kinds = kind;
    box.dataset.url = `${editor?.dataset.lookupUrl ?? `${base()}lookup`}${near ? `?near=${encodeURIComponent(near)}` : ""}`;
    const input = h("input", { type: "text", placeholder, autocomplete: "off" });
    box.append(input);
    box.addEventListener("p9-select", (event) => {
      const detail = (event as CustomEvent<{ id?: string; value: string }>).detail;
      if (detail.id) pick({ id: detail.id, title: detail.value });
    });
    return h("span", { class: "tpe-picker" }, box);
  }

  #section(label: string, hint: string, ...content: Array<Node | null>): HTMLElement {
    return h("div", { class: "tpe-section" }, h("span", { class: "tp-section", title: hint }, label), ...content);
  }

  // A small menu (the owner prefers menus to native selects): the current word, and the others to pick from.
  #menu(list: Word[], current: string, pick: (key: string) => void, label: string): HTMLElement {
    const now = list.find((w) => w.key === current);
    const menu = h("details", { class: "dropdown tpe-menu" }, h("summary", { class: "tp-status s-" + current, title: label, textContent: now?.label ?? current }));
    const items = h("div", { class: "dropdown-list opens-right", role: "menu" });
    for (const w of list) {
      const item = h("button", { type: "button", role: "menuitem", textContent: w.label });
      if (w.key === current) item.setAttribute("aria-current", "true");
      item.addEventListener("click", () => {
        menu.open = false;
        pick(w.key);
      });
      items.append(item);
    }
    menu.append(items);
    return menu;
  }

  #target(t: Target): HTMLElement {
    if (t.locked || !t.id) return h("span", { class: "tp-locked", textContent: t.title || "Something you can't see", title: "Kept as it is when you save" });
    const href = t.kind === "skein" ? `${base()}tapestry/skein/${t.id}` : `${base()}p/${t.id}/-`;
    return h("a", { class: t.kind === "skein" ? "tp-skein" : "", href, target: "_blank", textContent: t.title });
  }

  #remove(onRemove: () => void, what: string): HTMLElement {
    const button = h("button", { type: "button", class: "small link danger", textContent: "✕", title: `Remove ${what}` });
    button.addEventListener("click", () => onRemove());
    return button;
  }

  // One of the page's GMs (docs/tapestry-design.md §2.9): their new rows start hidden until revealed.
  #gm(): boolean {
    return this.closest<HTMLElement>("p9-editor")?.dataset.gm === "yes";
  }

  // Who a new row is for: only the GMs when a GM adds it (revealed later, by itself), else everyone who sees the box.
  #newTo(): string {
    return this.#gm() ? NOT_REVEALED : "";
  }

  // Who may see a row: for the GMs, first whether it's revealed at all; then @people and #groups, with suggestions as you
  // type and a menu of the usual choices.
  #restricted(row: { restrictedTo: string }): HTMLElement {
    const audience = this.#audience(row);
    if (!this.#gm() && row.restrictedTo.trim() !== NOT_REVEALED) return audience;
    const wrap = h("span", { class: "tpe-reveal" });
    const box = h("input", { type: "checkbox", checked: row.restrictedTo.trim() !== NOT_REVEALED, title: "Revealed: players who see the box see this row. Not ticked: only the GMs." });
    const label = h("label", { class: "small", title: box.title }, box, " Revealed");
    audience.hidden = !box.checked;
    box.addEventListener("change", () => {
      row.restrictedTo = box.checked ? "" : NOT_REVEALED;
      audience.hidden = !box.checked;
      const input = audience.querySelector("input");
      if (input) input.value = "";
      this.#changed(false);
    });
    wrap.append(label, audience);
    return wrap;
  }

  #audience(row: { restrictedTo: string }): HTMLElement {
    const editor = this.closest<HTMLElement>("p9-editor");
    const { element, input } = principalsInput({
      lookupUrl: editor?.dataset.lookupUrl ?? `${base()}lookup`, me: editor?.dataset.me, value: row.restrictedTo.trim() === NOT_REVEALED ? "" : row.restrictedTo,
      placeholder: "visible to all", everyone: "Everyone who sees the box",
      onInput: (value) => {
        row.restrictedTo = value;
        this.#changed(false);
      },
      onChange: () => {},
    });
    input.className = "tpe-restricted";
    input.title = "Only these @people and #groups see this row; empty: everyone who sees the box";
    return element;
  }

  // A page or skein, picked from suggestions in this page's frame (or wider, unticked).
  #picker(placeholder: string, pick: (target: Target) => void): HTMLElement {
    const editor = this.closest<HTMLElement>("p9-editor");
    const lookup = editor?.dataset.lookupUrl ?? `${base()}lookup`;
    const near = editor?.dataset.editingPage || editor?.dataset.pageId || "";
    const box = h("p9-autocomplete" as "div");
    box.dataset.kinds = "pages,skeins";
    const input = h("input", { type: "text", placeholder, autocomplete: "off" });
    const url = (within: string) => `${lookup}?near=${encodeURIComponent(near)}${within ? `&within=${within}` : ""}`;
    box.dataset.url = url("0");
    box.append(input);
    box.addEventListener("p9-select", (event) => {
      const detail = (event as CustomEvent<{ id?: string; value: string; kind?: string }>).detail;
      if (!detail.id) return;
      pick({ kind: detail.kind === "skein" ? "skein" : "page", id: detail.id, title: detail.value });
    });
    return h("span", { class: "tpe-picker" }, box, near ? scopeMenu(lookup, near, "frame", (within) => (box.dataset.url = url(within))) : null);
  }

  #involved(): HTMLElement {
    const b = this.#box;
    const rows = h("ul", { class: "tpe-rows" });
    for (const row of b.involved ?? []) {
      rows.append(row.fromText
        ? h("li", {}, h("span", { class: "tp-status", textContent: "mentioned" }), this.#target(row.target),
            h("span", { class: "tp-role", textContent: "from the text", title: "Linked in the box's text; involve it with a role below to say more" }))
        : h("li", {}, this.#menu(words!.roles.filter((w) => w.key !== "mentioned"), row.role, (key) => ((row.role = key), this.#changed()), "Role"),
            this.#target(row.target), this.#restricted(row), this.#remove(() => ((b.involved = b.involved!.filter((r) => r !== row)), this.#changed()), "from the thread")));
    }
    let role = "taking-part";
    const roleMenu = (): HTMLElement => this.#menu(words!.roles.filter((w) => w.key !== "mentioned"), role, (key) => {
      role = key;
      add.replaceChild(roleMenu(), add.firstChild!);
    }, "Role for the next one");
    const add = h("div", { class: "tpe-add" }, roleMenu(), this.#picker("Involve a page or skein…", (target) => {
      names.set(target.id!, target.title);
      b.involved = [...(b.involved ?? []).filter((r) => !(r.fromText && r.target.id === target.id)), { target, role, restrictedTo: this.#newTo() }];
      this.#changed();
    }));
    return this.#section("Involved", "Who's behind it, its targets, who takes part, who's affected, where. Pages linked in the box count as mentioned.", rows, add);
  }

  #stitches(): HTMLElement {
    const b = this.#box;
    const list = h("ol", { class: "tpe-stitches" });
    (b.stitches ?? []).forEach((s, i) => {
      const titleInput = h("input", { type: "text", value: s.title, maxLength: 200, class: "tpe-stitch-title" });
      titleInput.addEventListener("input", () => ((s.title = titleInput.value), this.#changed(false)));
      const move = (by: number) => {
        const all = b.stitches!;
        const to = i + by;
        if (to < 0 || to >= all.length) return;
        [all[i], all[to]] = [all[to]!, all[i]!];
        this.#changed();
      };
      const up = h("button", { type: "button", class: "small", textContent: "↑", title: "Move up", disabled: i === 0 });
      up.addEventListener("click", () => move(-1));
      const down = h("button", { type: "button", class: "small", textContent: "↓", title: "Move down", disabled: i === b.stitches!.length - 1 });
      down.addEventListener("click", () => move(1));
      const notes = h("textarea", { rows: 2, value: s.body, placeholder: "Notes (Markdown)" });
      notes.addEventListener("input", () => ((s.body = notes.value), this.#changed(false)));
      const more = h("details", { class: "tpe-more", open: s.body.length > 0 || s.knows.length > 0 || Boolean(s.when || s.until || s.by) },
        h("summary", { textContent: "Notes, who knows, times, who may see it" }),
        notes,
        this.#knowsSection(s.knows, "Who knows of this stitch", false),
        this.#waitsFor(s, b.stitches ?? []),
        h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Times:" }),
          this.#timeInput(s.when ?? "", "when", "When it happens, or starts (optional): the party at 1998-11-07 18:00", (value) => ((s.when = value), this.#changed(false))),
          this.#timeInput(s.until ?? "", "until", "Until when, for one that takes a while (optional): to 1998-11-08 02:00", (value) => ((s.until = value), this.#changed(false))),
          this.#timeInput(s.by ?? "", "deadline (by)", "Must be done by (optional): listed as overdue once Now passes it", (value) => ((s.by = value), this.#changed(false)))),
        h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Visible to " }), this.#restricted(s)));
      list.append(h("li", {},
        h("div", { class: "tpe-stitch" }, this.#menu(words!.stitchStatuses, s.status, (key) => ((s.status = key), this.#changed()), "Status"), titleInput, up, down,
          this.#remove(() => ((b.stitches = b.stitches!.filter((x) => x !== s)), this.#changed()), "this stitch")),
        more));
    });
    const input = h("input", { type: "text", placeholder: "Next step, e.g. Win over the Sheriff", maxLength: 200 });
    const addStitch = () => {
      if (!input.value.trim()) return;
      b.stitches = [...(b.stitches ?? []), { id: newId(), title: input.value.trim(), body: "", status: "to-stitch", restrictedTo: this.#newTo(), noAi: false, knows: [] }];
      this.#changed();
    };
    input.addEventListener("keydown", (event) => event.key === "Enter" && addStitch());
    const button = h("button", { type: "button", class: "small", textContent: "Add stitch" });
    button.addEventListener("click", addStitch);
    return this.#section("Stitches", "The steps someone takes to pursue this thread, in order.", list, h("div", { class: "tpe-add" }, input, button));
  }

  // A stitch waiting for others of the same thread.
  #waitsFor(s: Stitch, all: Stitch[]): HTMLElement | null {
    const others = all.filter((o) => o !== s);
    if (others.length === 0) return null;
    const wait = new Set(s.waitsFor ?? []);
    const boxes = others.map((o) => {
      const tick = h("input", { type: "checkbox", checked: wait.has(o.id) });
      tick.addEventListener("change", () => {
        if (tick.checked) wait.add(o.id);
        else wait.delete(o.id);
        s.waitsFor = [...wait];
        this.#changed(false);
      });
      return h("label", { class: "small tpe-wait" }, tick, ` ${o.title}`);
    });
    return h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Waits for:" }), ...boxes);
  }

  // The page's ties: its own (going out) to change, and those coming in to see, with one-sided ones pointed out.
  #ties(): HTMLElement {
    const b = this.#box;
    const kindWords = (): Word[] => [{ key: "", label: "some relation (not set)", themed: "" }, ...tieKinds.map((k) => ({ key: k.id, label: k.label, themed: k.label }))];
    const newKind = h("button", { type: "button", class: "small link", textContent: "+ new kind", title: "A kind of tie not in the list, e.g. “owes a boon to”" });
    newKind.addEventListener("click", async () => {
      const label = window.prompt("Name the new kind of tie, as it reads from this page (e.g. “owes a boon to”):")?.trim();
      if (!label) return;
      const similar = tieKinds.filter((k) => k.label.toLowerCase().includes(label.toLowerCase().slice(0, 4)));
      if (similar.length && !window.confirm(`Similar kinds exist: ${similar.map((k) => k.label).join(", ")}.\n\nMake “${label}” anyway?`)) return;
      const inverse = window.prompt(`How does it read from the other end? (e.g. “is owed a boon by”; leave empty if it's the same both ways)`)?.trim() ?? "";
      const response = await fetch(`${base()}tapestry/state?handler=NewKind`, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: document.querySelector<HTMLMetaElement>('meta[name="request-token"]')?.content ?? "" },
        body: JSON.stringify({ label, inverse }),
      });
      if (!response.ok) return void window.alert(((await response.json().catch(() => ({}))) as { error?: string }).error ?? "Couldn't make the kind.");
      const kind = (await response.json()) as TieKind;
      if (!tieKinds.some((k) => k.id === kind.id)) tieKinds = [...tieKinds, kind];
      this.#render();
    });

    const rows = h("ul", { class: "tpe-rows" });
    for (const row of b.ties ?? []) {
      const note = h("input", { type: "text", value: row.note, placeholder: "note, e.g. a major debt", maxLength: 1000 });
      note.addEventListener("input", () => ((row.note = note.value), this.#changed(false)));
      const threads: Word[] = [{ key: "", label: "not from a thread", themed: "" }, ...(b.threads ?? []).map((t) => ({ key: t.id, label: `from ${t.title}`, themed: t.title }))];
      rows.append(h("li", {},
        this.#menu(kindWords(), row.kindId ?? "", (key) => ((row.kindId = key || null), (row.kindLabel = null), this.#changed()), "Kind of tie"),
        this.#target(row.target), note,
        (b.threads ?? []).length ? this.#menu(threads, row.threadId ?? "", (key) => ((row.threadId = key || null), this.#changed()), "The thread it came from") : null,
        this.#restricted(row), this.#remove(() => ((b.ties = b.ties!.filter((r) => r !== row)), this.#changed()), "this tie"),
        h("details", { class: "tpe-more", open: Boolean(row.since || row.until) }, h("summary", { textContent: "times, who knows of it" }),
          h("div", { class: "tpe-add" }, h("span", { class: "muted small", textContent: "Times:" }),
            this.#timeInput(row.since ?? "", "since", "Since when it holds (optional), e.g. since the flood", (value) => ((row.since = value), this.#changed(false))),
            this.#timeInput(row.until ?? "", "until", "Until when (optional)", (value) => ((row.until = value), this.#changed(false)))),
          this.#knowsSection(row.knows, "Who knows of this tie", false))));
    }
    let kindId = "";
    const kindMenu = (): HTMLElement => this.#menu(kindWords(), kindId, (key) => {
      kindId = key;
      add.replaceChild(kindMenu(), add.firstChild!);
    }, "Kind for the next tie");
    const add = h("div", { class: "tpe-add" }, kindMenu(), this.#picker("Tie this page to…", (target) => {
      if (target.id === b.id.replace(/^ties:/, "")) return;
      names.set(target.id!, target.title);
      b.ties = [...(b.ties ?? []), { id: newId(), target, kindId: kindId || null, kindLabel: null, note: "", threadId: null, restrictedTo: this.#newTo(), noAi: false, knows: [] }];
      this.#changed();
    }), newKind);

    const incoming = (b.incoming ?? []).map((t) => h("li", {}, h("span", { class: "tp-tie-reads", textContent: t.reads }), this.#target(t.from),
      t.via ? h("span", { class: "tp-role", textContent: `via ${t.via}` }) : null, t.note ? h("span", { class: "tp-note-inline", textContent: t.note }) : null));
    const gaps = (b.unanswered ?? []).filter((u) => !(b.ties ?? []).some((t) => t.target.id === u.id)).map((u) => {
      const set = h("button", { type: "button", class: "small", textContent: "+ add", title: "Add how this page sees them" });
      set.addEventListener("click", () => {
        b.ties = [...(b.ties ?? []), { id: newId(), target: u, kindId: null, kindLabel: null, note: "", threadId: null, restrictedTo: this.#newTo(), noAi: false, knows: [] }];
        this.#changed();
      });
      return h("li", {}, h("span", { class: "muted small", textContent: "How this page sees " }), this.#target(u), h("span", { class: "muted small", textContent: ": not set yet" }), set);
    });
    const head = h("div", { class: "tpe-head" }, h("span", { class: "tp-kind" }, "Ties of this page"),
      h("a", { class: "tpe-help", href: `${base()}tapestry/help`, target: "_blank", title: "How ties work", textContent: "?" }));
    return h("div", { class: "tpe" }, head,
      this.#section("Going out", "How this page stands with others: saved with the page.", rows, add),
      incoming.length ? this.#section("Coming in", "Ties others have to this page (change them on their pages).", h("ul", { class: "tpe-rows" }, ...incoming)) : h("span"),
      gaps.length ? this.#section("One-sided", "Ties coming in with nothing going back.", h("ul", { class: "tpe-rows" }, ...gaps)) : h("span"));
  }

  // Who knows of something; under a stitch or a tie these rows are seen by whoever sees it (no restriction of their own).
  #knowsSection(rows: Knows[], label: string, restrictable = true): HTMLElement {
    const list = h("ul", { class: "tpe-rows" });
    for (const row of rows) {
      const note = h("input", { type: "text", value: row.note, placeholder: "note", maxLength: 500 });
      note.addEventListener("input", () => ((row.note = note.value), this.#changed(false)));
      list.append(h("li", {}, this.#target(row.target), this.#menu(words!.knowledge, row.state, (key) => ((row.state = key), this.#changed()), "Knows"), note,
        restrictable ? this.#restricted(row) : null, this.#remove(() => (rows.splice(rows.indexOf(row), 1), this.#changed()), "this row")));
    }
    const add = h("div", { class: "tpe-add" }, this.#picker("Who knows: a page or skein…", (target) => {
      names.set(target.id!, target.title);
      rows.push({ target, state: "knows", note: "", restrictedTo: this.#newTo() });
      this.#changed();
    }));
    return this.#section(label, "What characters know in the story; it never changes who can read anything.", list, add);
  }

  #looseEnds(): HTMLElement {
    const b = this.#box;
    const list = h("ul", { class: "tpe-rows" });
    for (const row of b.looseEnds ?? []) {
      const text = h("input", { type: "text", value: row.text, maxLength: 1000, class: "tpe-wide" });
      text.addEventListener("input", () => ((row.text = text.value), this.#changed(false)));
      const states = row.kind === "risk" ? words!.riskStates : words!.todoStates;
      const kinds: Word[] = [{ key: "todo", label: "to-do", themed: "to-do" }, { key: "risk", label: "risk", themed: "risk" }];
      const forWho = h("input", { type: "text", value: row.for, placeholder: "for @who", class: "tpe-for", title: "Who it's for (optional)" });
      forWho.addEventListener("input", () => ((row.for = forWho.value.trim()), this.#changed(false)));
      list.append(h("li", {},
        this.#menu(kinds, row.kind, (key) => ((row.kind = key), (row.state = "open"), this.#changed()), "Kind"), text,
        this.#menu(states, row.state, (key) => ((row.state = key), this.#changed()), "State"), forWho, this.#restricted(row),
        this.#remove(() => ((b.looseEnds = b.looseEnds!.filter((x) => x !== row)), this.#changed()), "this loose end")));
    }
    const input = h("input", { type: "text", placeholder: "Something to keep in mind (a to-do, or a risk)…", maxLength: 1000, class: "tpe-wide" });
    const addEnd = (kind: string) => {
      if (!input.value.trim()) return;
      b.looseEnds = [...(b.looseEnds ?? []), { id: newId(), text: input.value.trim(), kind, state: "open", for: "", restrictedTo: this.#newTo(), noAi: false }];
      this.#changed();
    };
    input.addEventListener("keydown", (event) => event.key === "Enter" && addEnd("todo"));
    const todo = h("button", { type: "button", class: "small", textContent: "Add to-do" });
    todo.addEventListener("click", () => addEnd("todo"));
    const risk = h("button", { type: "button", class: "small", textContent: "Add risk" });
    risk.addEventListener("click", () => addEnd("risk"));
    return this.#section("Loose ends", "The GM's to-dos (ticked off) and risks (lived with until they're gone).", list, h("div", { class: "tpe-add" }, input, todo, risk));
  }

  #members(): HTMLElement {
    const b = this.#box;
    const list = h("ul", { class: "tpe-rows" });
    for (const row of b.members ?? []) {
      const li = h("li", {}, this.#target(row.target));
      if (row.fromText) li.append(h("span", { class: "tp-role", textContent: "from the text" }));
      else li.append(this.#restricted(row), this.#remove(() => {
        b.members = b.members!.filter((r) => r !== row);
        this.#changed();
        // An empty skein is fine as work in progress, but offer to remove it once its last member is gone.
        if (b.members.filter((m) => !m.fromText).length === 0 && window.confirm("That was the skein's last member. Remove the whole skein from the page?\n\nOK removes its box (it can then be deleted for good from the page's Tapestry list). Cancel keeps it empty, to fill later."))
          this.dispatchEvent(new CustomEvent("p9-tapestry-remove-box", { bubbles: true, detail: { id: b.id } }));
      }, "from the skein"));
      list.append(li);
    }
    const add = h("div", { class: "tpe-add" }, this.#picker("Add a page or skein…", (target) => {
      if (target.id === b.id) return;
      names.set(target.id!, target.title);
      b.members = [...(b.members ?? []).filter((r) => !(r.fromText && r.target.id === target.id)), { target, restrictedTo: this.#newTo() }];
      this.#changed();
    }));
    return this.#section("Members", "Pages (or other skeins) in this group. Pages linked in the box are members too.", list, add);
  }
}

customElements.define("p9-tapestry-box", P9TapestryBox);
